import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { SEO } from '../lib/seo';
import { fetchCmsList } from '../lib/cms';

export function BlogDetail({ settings }) {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFullImage, setShowFullImage] = useState(false);

  useEffect(() => {
    async function load() {
      setLoading(true);
      const allArticles = await fetchCmsList('articles');
      
      const found = allArticles.find((a) => {
        const aSlug = a.slug?.da || a.slug || '';
        return (
          aSlug === slug ||
          a.id === slug ||
          a.title?.toLowerCase().replace(/[^a-z0-9]+/g, '-').includes(slug.toLowerCase()) ||
          slug.includes(a.title?.toLowerCase().replace(/[^a-z0-9]+/g, '-'))
        );
      });

      if (found) {
        setArticle(found);
        setRelatedArticles(allArticles.filter((a) => a.id !== found.id).slice(0, 3));
      }
      setLoading(false);
    }
    load();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center py-20 bg-canvas">
        <div className="text-center space-y-3">
          <i className="fa-solid fa-circle-notch fa-spin text-3xl text-brand-accent" aria-hidden="true" />
          <p className="text-xs font-semibold uppercase tracking-wider text-brand-muted">Henter artikel...</p>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-[60vh] max-w-2xl mx-auto px-4 py-20 text-center space-y-6 bg-canvas">
        <h1 className="font-serif text-3xl font-bold text-brand-charcoal">Artiklen blev ikke fundet</h1>
        <p className="text-brand-muted text-sm">
          Vi kunne desværre ikke finde den artikel du ledte efter.
        </p>
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 bg-brand-charcoal text-canvas font-bold px-6 py-3 rounded-full text-xs uppercase tracking-wider"
        >
          <span>Se alle artikler</span>
        </Link>
      </div>
    );
  }

  const phone = settings?.phone || "82 82 10 10";
  const articleImage = article.image?.[0] || article.featured_image?.[0];
  const displayDate = article.date || article.publish_date || "2026";

  return (
    <>
      <SEO 
        title={`${article.title} | Fodtid`}
        description={article.summary || article.title}
        image={articleImage}
      />

      {/* Header / Breadcrumb */}
      <article className="bg-canvas pb-20 border-b border-brand-border">
        <header className="bg-canvas py-12 sm:py-16 border-b border-brand-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 space-y-6">
            
            <div className="flex items-center gap-2 text-xs font-semibold text-brand-muted">
              <Link to="/" className="hover:text-brand-charcoal">Forside</Link>
              <span>/</span>
              <Link to="/blog" className="hover:text-brand-charcoal">Blog</Link>
              <span>/</span>
              <span className="text-brand-charcoal truncate max-w-xs">{article.title}</span>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-3 text-xs text-brand-muted flex-wrap">
                <span className="font-semibold text-brand-charcoal bg-surface px-2.5 py-0.5 rounded-full border border-brand-border">
                  {article.category || "Fodpleje info"}
                </span>
                {displayDate && (
                  <span>{displayDate}</span>
                )}
                <span>•</span>
                <span>{article.author || "Klaus Just"}</span>
              </div>

              <h1 className="font-serif text-3xl sm:text-4xl font-bold text-brand-charcoal leading-tight">
                {article.title}
              </h1>
            </div>

          </div>
        </header>

        <div className="max-w-7xl mx-auto px-4 sm:px-8 pt-10 sm:pt-14">
          
          {/* Main article image / graphic - uncropped with object-contain for full sharpness */}
          {articleImage && (
            <div className="mb-10 max-w-4xl rounded-3xl overflow-hidden bg-[#FAF7F2] p-3 sm:p-6 border border-brand-border shadow-xs">
              <div className="relative group flex items-center justify-center">
                <img
                  src={articleImage}
                  alt={article.title}
                  className="w-full h-auto max-h-[85vh] object-contain rounded-2xl cursor-zoom-in transition-transform duration-200 group-hover:scale-[1.01]"
                  onClick={() => setShowFullImage(true)}
                />
                <button
                  type="button"
                  onClick={() => setShowFullImage(true)}
                  className="absolute bottom-3 right-3 bg-stone-900/85 hover:bg-stone-900 text-white text-xs font-semibold px-3.5 py-1.5 rounded-full backdrop-blur-xs flex items-center gap-1.5 shadow transition-colors"
                >
                  <i className="fa-solid fa-magnifying-glass-plus" aria-hidden="true" />
                  <span>Se i fuld skærm</span>
                </button>
              </div>
            </div>
          )}

          {/* Full-screen Lightbox Modal */}
          {showFullImage && articleImage && (
            <div 
              className="fixed inset-0 z-50 bg-black/92 backdrop-blur-md flex items-center justify-center p-4 sm:p-8 animate-in fade-in duration-200"
              onClick={() => setShowFullImage(false)}
            >
              <button
                type="button"
                onClick={() => setShowFullImage(false)}
                className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors text-lg focus:outline-none"
                aria-label="Luk fuld skærm"
              >
                <i className="fa-solid fa-xmark" aria-hidden="true" />
              </button>
              <img
                src={articleImage}
                alt={article.title}
                className="max-w-full max-h-[92vh] object-contain rounded-lg shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}

          {/* Verbatim Body Content */}
          <div 
            className="prose prose-stone prose-lg max-w-none prose-headings:font-serif prose-headings:text-brand-charcoal prose-a:text-brand-accent prose-strong:text-brand-charcoal prose-li:text-brand-muted"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Author Bio Box */}
          <div className="mt-14 p-6 sm:p-8 bg-surface/50 rounded-3xl border border-brand-border flex flex-col sm:flex-row items-center gap-6 shadow-xs">
            <div className="w-20 h-20 rounded-full overflow-hidden shrink-0 border-2 border-brand-border shadow-xs">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=90,format=auto/assets/d4428667-cde9-4c31-9224-88278506a01a/cropped-skaermbillede-2025-09-03-kl-17-07-56.png"
                alt="Klaus Just"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="space-y-1.5 text-center sm:text-left">
              <div className="text-xs uppercase font-bold tracking-wider text-brand-muted">Skrevet af</div>
              <div className="font-serif text-lg font-bold text-brand-charcoal">Klaus Just</div>
              <p className="text-xs text-brand-muted leading-relaxed">
                Uddannet fodbehandler med over 15 års erfaring og mere end 8.000 behandlede fødder. Klaus tilbyder professionel, skånsom fodpleje direkte i dit eget hjem.
              </p>
              <div className="pt-2">
                <Link to="/kontakt" className="text-xs font-bold text-brand-charcoal hover:text-brand-accent underline">
                  Book tid hos Klaus →
                </Link>
              </div>
            </div>
          </div>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <div className="mt-16 pt-12 border-t border-brand-border space-y-6">
              <h3 className="font-serif text-2xl font-bold text-brand-charcoal">
                Læs også andre artikler
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {relatedArticles.map((rel) => {
                  const relSlug = typeof rel.slug === 'object' ? (rel.slug?.da || Object.values(rel.slug || {})[0] || rel.id) : (rel.slug || rel.id);
                  return (
                    <Link
                      key={rel.id || relSlug}
                      to={`/blog/${relSlug}`}
                      className="bg-surface/50 rounded-2xl p-5 border border-brand-border hover:border-brand-accent transition-all group block shadow-xs"
                    >
                      <div className="text-[10px] font-bold uppercase tracking-wider text-brand-muted mb-1">
                        {rel.category || 'Fodpleje info'}
                      </div>
                      <div className="font-serif text-sm font-bold text-brand-charcoal group-hover:text-brand-accent transition-colors line-clamp-2">
                        {rel.title}
                      </div>
                      <div className="text-[11px] text-brand-muted line-clamp-2 mt-2">
                        {rel.summary || rel.excerpt}
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* Bottom Back Button */}
          <div className="mt-12 text-center">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-brand-charcoal bg-surface hover:bg-surface-soft border border-brand-border px-6 py-3 rounded-full transition-colors"
            >
              <i className="fa-solid fa-arrow-left" aria-hidden="true" />
              <span>Tilbage til oversigten over artikler</span>
            </Link>
          </div>

        </div>
      </article>
    </>
  );
}
